<template>
    <div class="edit-resume-import-upload-v2">
        <div
            :aria-label="$t('profile.experience.funnel.step.upload.dropzone.description')"
            class="edit-resume-import-upload-v2__dropzone-wrapper"
            role="button"
            tabindex="0"
            @keydown="handleDropzoneKeydown"
        >
            <VJoyDropzone
                :accept="ruleDropzoneFileFormats"
                :error-text-max-size="$t('general.errors.upload.file.maxsize', [`${ruleDropzoneMaxFileSize}`])"
                :invalid="invalid"
                :max-size="ruleDropzoneMaxFileSize"
                :model-value="dropzoneFiles"
                class="edit-resume-import-upload-v2__dropzone"
                data-testid="profile-common-resume-import-upload-dropzone"
                name="profileUploadResume"
                @update:model-value="(value: File[]) => (dropzoneFiles = value)"
            >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <template #dropzone-description><span v-html="$t('profile.experience.funnel.step.upload.dropzone.description.v2')" /></template>
                <template #dropzone-legend>
                    <VJoyFormError v-if="invalid" :errors="[$t('profile.experience.funnel.step.upload.error.no-file')]" no-margin />
                    <template v-else>
                        {{ $t('profile.experience.funnel.step.upload.dropzone.formats') }} {{ acceptedFileFormatNames.join(', ') }}
                    </template>
                </template>
            </VJoyDropzone>
        </div>

        <slot name="cta" />

        <hr class="edit-resume-import-upload-v2__separator" />

        <div class="edit-resume-import-upload-v2__linkedin">
            <div class="edit-resume-import-upload-v2__linkedin-content">
                <div class="edit-resume-import-upload-v2__linkedin-header">
                    <MaltImg alt="Linkedin logo" height="20" src="/profile/edition/linkedin-logo.svg" width="20" />
                    <span class="joy-font-primary-450">{{ $t('profile.experience.funnel.step.upload.help.linkedin.title') }}</span>
                </div>
                <ol class="edit-resume-import-upload-v2__linkedin-steps">
                    <li>
                        {{ $t('profile.experience.funnel.step.upload.help.linkedin.description1') }}
                        <VJoyLink href="https://www.linkedin.com/in/" rel="noopener noreferrer" target="_blank">
                            {{ $t('profile.experience.funnel.step.upload.help.linkedin.step1.link') }}
                        </VJoyLink>
                    </li>
                    <li>{{ $t('profile.experience.funnel.step.upload.help.linkedin.step2') }}</li>
                    <li>{{ $t('profile.experience.funnel.step.upload.help.linkedin.step3') }}</li>
                </ol>
            </div>
            <MaltImg
                :src="imgUrlHelpLinkedInExport"
                alt="Linkedin import explanation"
                class="edit-resume-import-upload-v2__linkedin-image"
                width="240"
            />
        </div>
    </div>
</template>
<script lang="ts" setup>
    import {computed, ref, toRefs, watch} from 'vue';
    import {MaltImg} from '#components';
    import {useLocale} from '#imports';
    import {VJoyDropzone, VJoyFormError, VJoyLink} from '@maltjoy/core-vue';

    type EditResumeImportUploadFileV2Props = {
        ruleDropzoneFileFormats?: string[];
        ruleDropzoneMaxFileSize?: number;
        invalid?: boolean;
    };

    const props = withDefaults(defineProps<EditResumeImportUploadFileV2Props>(), {
        ruleDropzoneFileFormats: () => ['application/pdf'],
        ruleDropzoneMaxFileSize: 5,
        invalid: false,
    });

    const emit = defineEmits<{
        'update:model-value': [value: File | undefined];
    }>();

    const {locale} = useLocale();
    const {ruleDropzoneFileFormats, ruleDropzoneMaxFileSize} = toRefs(props);
    const dropzoneFiles = ref<File[]>([]);

    const dropzoneSingleFile = computed((): File | undefined => (dropzoneFiles.value.length > 0 ? dropzoneFiles.value[0] : undefined));
    const isFileValid = computed(
        (): boolean =>
            !!dropzoneSingleFile.value &&
            ruleDropzoneFileFormats.value.includes(dropzoneSingleFile.value.type) &&
            dropzoneSingleFile.value.size <= ruleDropzoneMaxFileSize.value * 1024 * 1024,
    );
    const acceptedFileFormatNames = computed(() => ruleDropzoneFileFormats.value.map((format) => format.split('/')[1]));
    const imgUrlHelpLinkedInExport = computed(() => {
        const existingTranslations = ['de', 'en', 'es', 'fr', 'nl'];
        const lang = locale.value.split('-')[0];
        const language = existingTranslations.includes(lang) ? lang : 'en';
        return `/profile/edition/linkedin_resume_v2_${language}.png`;
    });

    watch(dropzoneSingleFile, () => {
        const file = dropzoneSingleFile.value && isFileValid.value ? dropzoneSingleFile.value : undefined;
        emit('update:model-value', file);
    });

    function handleDropzoneKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        (event.currentTarget as HTMLElement).querySelector<HTMLInputElement>('input[type="file"]')?.click();
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .edit-resume-import-upload-v2 {
        display: flex;
        flex-direction: column;
        gap: var(--joy-core-spacing-6);

        &__dropzone-wrapper {
            &:focus-visible {
                outline: var(--joy-core-border-width-focus) solid var(--joy-color-primary-50);
                border-radius: var(--joy-core-radius-3);
            }
        }

        &__dropzone {
            .joy-dropzone__instruction {
                height: 198px;
                flex-direction: column;
                justify-content: center;
            }

            .joy-dropzone__file {
                height: 166px;
            }
        }

        &__linkedin {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            gap: var(--joy-core-spacing-6);

            @include mq.screen_xs {
                flex-direction: column;
            }
        }

        &__linkedin-content {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-3);
            flex: 1;
        }

        &__linkedin-header {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: var(--joy-core-spacing-2);
        }

        &__linkedin-steps {
            margin: 0;
            padding-left: var(--joy-core-spacing-5);
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-2);
        }

        &__linkedin-image {
            width: 240px;
            flex-shrink: 0;

            @include mq.screen_xs {
                width: 100%;
            }
        }

        &__browse-link {
            color: var(--joy-color-secondary-50);
            font-weight: var(--joy-font-weight-bold);
            cursor: pointer;
        }

        &__separator {
            width: 100%;
            border: none;
            border-top: 1px solid var(--joy-color-border-default);
            margin: 0;
        }
    }
</style>
